import { Component, ViewChild, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, FormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';

import { LeaseService, Lease } from 'src/shared/services/lease.service';
import { rentLabel as sharedRentLabel, propertyLabel as sharedPropertyLabel } from 'src/shared/util/rent-label';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// Precomputed mobile-card view model — templates never call functions (CLAUDE.md rule).
export interface LeaseRowView {
  lease: Lease;
  propertyLabel: string;
  rentLabel: string;
}

@Component({
  selector: 'leases-content',
  templateUrl: './leases-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LeasesContentComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private leaseService = inject(LeaseService);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);

  readonly windowService = inject(WindowService);

  searchForm: UntypedFormGroup;
  leases: Lease[] = [];
  headers: string[] = ['Tenant', 'Property', 'Term', 'Rent', 'Balance', 'Status', 'last-column'];
  allRows: any[] = [];
  shownRows: any[] = [];
  emptyStateMessage = 'No leases yet.';
  shownLeaseViews: LeaseRowView[] = [];
  loaded = false;
  errorMessage: string;
  private panelClosedSubscription: Subscription;

  @ViewChild('tidyTable') tidyTable: any;

  constructor() {
    this.searchForm = this.fb.group({ search: [''] });
    this.searchForm.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load leases';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.leases = await this.leaseService.getLeases();
    this.allRows = this.buildRows(this.leases);
    this.shownRows = [...this.allRows];
    this.shownLeaseViews = this.buildLeaseViews(this.leases);
    this.loaded = true;
    this.cdr.markForCheck();
  }

  buildRows(leases: Lease[]): any[] {
    return leases.map(l => {
      const balance = this.leaseService.balanceFor(l);
      return {
        id: l.id,
        'Tenant': { value: l.tenant_name, initials: true },
        'Property': { value: this.propertyLabel(l) },
        'Term': { value: `${this.formatDate(l.start_date)  } – ${  this.formatDate(l.end_date)}` },
        'Rent': { value: this.rentLabel(l) },
        'Balance': { value: this.leaseService.balanceLabel(balance) },
        'Status': { value: l.status },
        'last-column': { value: '', hasChevron: true },
      };
    });
  }

  // Precomputed mobile-card view models — templates never call functions (CLAUDE.md rule).
  // Rebuilt here and in updateSearch() wherever the shown list changes.
  buildLeaseViews(leases: Lease[]): LeaseRowView[] {
    return leases.map(lease => ({
      lease,
      propertyLabel: this.propertyLabel(lease),
      // Mobile card appends '/mo' itself when not weekly (see template), matching
      // the desktop Rent column's existing (no-'/mo'-for-monthly) display exactly.
      rentLabel: this.rentLabel(lease),
    }));
  }

  // Precomputed so templates never call functions. For room leases `unit` is already the room
  // label (e.g. "Room A") matching `space_name`; only append `space_name` when it differs so we
  // don't duplicate the room name in the cell.
  propertyLabel(lease: Lease): string {
    return sharedPropertyLabel(lease.property_name, lease.unit, lease.space_name);
  }

  // Weekly room leases show the raw weekly figure; monthly leases show the bare
  // amount here — the '/mo' suffix for monthly is appended by the caller (desktop
  // table renders it bare; mobile card appends '/mo' itself), matching the
  // existing desktop Rent column display exactly.
  rentLabel(lease: Lease): string {
    return lease.cadence === 'weekly' && lease.weekly_rent
      ? sharedRentLabel(lease.weekly_rent, 'weekly')
      : `$${  lease.monthly_rent.toLocaleString('en-US')}`;
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }

  balanceFor(lease: Lease): number { return this.leaseService.balanceFor(lease); }
  balanceColor(balance: number): string { return this.leaseService.balanceColor(balance); }
  balanceLabel(balance: number): string { return this.leaseService.balanceLabel(balance); }
  statusColor(status: any): string { return this.leaseService.statusColor(status); }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      this.shownLeaseViews = this.buildLeaseViews(this.leases);
      this.emptyStateMessage = 'No leases yet.';
      this.cdr.markForCheck();
      return;
    }
    this.emptyStateMessage = 'No leases match your search.';
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      row['Tenant'].value.toLowerCase().includes(term) ||
      row['Property'].value.toLowerCase().includes(term)
    );
    const shownIds = new Set(this.shownRows.map(row => row.id));
    this.shownLeaseViews = this.buildLeaseViews(this.leases.filter(l => shownIds.has(l.id)));
    this.cdr.markForCheck();
  }

  onRowClick(event: { column: string; element: any }): void {
    const lease = this.leases.find(l => l.id === event.element.id);
    if (lease) { this.goToPage('lease-ledger', { lease }, LeaseLedgerPage); }
  }

  async goToPage(url: string, params: any, component: any): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => { await this.loadPage(); });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
